import { Component, Input, NgZone, OnDestroy } from "@angular/core";
import moment from 'moment';
import { Subscription } from "rxjs";
import { ImpersonationService } from "../../../../auth/apigee/impersonation.service";
import { LoggerService } from "../../../../common/logging/logger.service";
import { ModalDialogComponent } from "../../../../common/modal/modalDialog.component";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import { VisitsCallsTargetsService, VisitsCallsTargetsServiceData } from "../../services/visitsCallsTargetsKpi/visitsCallsTargets.service";
import { ColumnDefinition, DateColumnDefinition, TextColumnDefinition, NumberColumnDefinition } from "../../../../dashboardCommon/components/cardDetails/datatable.component";
import { SingleSummaryDetailKpiServiceHost } from "../../../../dashboardCommon/components/kpiServiceHostComponent";

export class OfferingsData {
    public consultant: number;
    public target: number;
    public progress: number;

    constructor(consultant: number) {
        this.consultant = consultant;
        this.target = 20;
        this.progress = this.target != 0 ? Math.min(100, consultant / this.target * 100) : 100;
    }
}

export class OfferingsDetailData {
    public columnDefinitions: ColumnDefinition[];
    public tabledata: any[][];

    constructor(columnDefinitions: ColumnDefinition[], tabledata: any[][]) {
        this.columnDefinitions = columnDefinitions;
        this.tabledata = tabledata;
    }
}

@Component({
    selector: 'offerings-kpi-service-host',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class OfferingsKpiServiceHostComponent extends SingleSummaryDetailKpiServiceHost<VisitsCallsTargetsService, VisitsCallsTargetsServiceData, OfferingsData, OfferingsDetailData>
    implements OnDestroy {
    public getClassName() { return "OfferingsKpiServiceHostComponent"; }

    private raasSubscription: Subscription;
    private officeSelectorSubscription: Subscription;
    @Input() public detailDialog: ModalDialogComponent;
    @Input() public messageDialog: ModalDialogComponent;

    constructor(zone: NgZone, logger: LoggerService, private impersonationService: ImpersonationService, private officeSelectorService: OfficeSelectorService,
        summaryService: VisitsCallsTargetsService) {
        super(zone, logger, summaryService);
        this.raasSubscription = this.impersonationService.impersonationChanged.subscribe(() => { this.resetSummary(); });
        this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => { this.resetSummary(); });
    }

    protected processSummaryData(data: VisitsCallsTargetsServiceData, updateRequested: Boolean): OfferingsData {

        if (data.consultantEntry) {
            let thisWeek = moment().isoWeek();
            let updateTime = this.extractUpdateTime(data);
            let now = moment();
            if (updateTime && moment(updateTime).isoWeek() == thisWeek) {
                let diff = moment.duration(now.diff(moment(data.consultantEntry.visitsUpdateTimestamp)));
                if (diff.hours() < 2 && this.summaryData && updateRequested) {
                    this.messageDialog.open();
                }

                let consultant = (data.consultantEntry.offerings || []).filter(x => moment(x.updateTimestamp).isoWeek() == thisWeek).length;

                return new OfferingsData(consultant);
            } else {
                return this.getDefaultSummaryData();
            }
        }
        else
        {
            return new OfferingsData(0);
        }

    }

    protected getDefaultSummaryData(): OfferingsData {
        let data = new OfferingsData(0);
        return data;
    }

    protected extractUpdateTime(data: VisitsCallsTargetsServiceData): Date {
        if (data.consultantEntry) {
            return data.consultantEntry.offeringsUpdateTimestamp;
        } else {
            return new Date();
        }
    }

    protected processDetailData(data: VisitsCallsTargetsServiceData, args: any): OfferingsDetailData {
        let thisWeek = moment().isoWeek();

        let columnDefinitions = [
            new TextColumnDefinition("Cliente", null, true),
            new TextColumnDefinition("Segmento", null, true),
            new DateColumnDefinition("Fecha", (data) => moment(data).format("DD/MM/YYYY")),
            new NumberColumnDefinition("Nº ofrecimientos"),
            new NumberColumnDefinition("Nº candidatos"),
            new TextColumnDefinition("Puesto", null, true),
        ];

        let tabledata = [];


        let offerings = (data.consultantEntry ? (data.consultantEntry.offerings || []) : [])
            .filter(x => moment(x.updateTimestamp).isoWeek() == thisWeek)
            .sort(function (a, b) {
                if (a.date > b.date) {
                    return 1;
                }
                if (a.date < b.date) {
                    return -1;
                }
                // a must be equal to b
                return 0;
            });

        for (let offering of offerings) {
            tabledata.push(
                [
                    offering.clientName,
                    "S" + offering.segment,
                    moment(offering.date).startOf("day"),
                    offering.offeringCount,
                    offering.candidateCount,
                    offering.jobTitle
                ]);
        }

        return new OfferingsDetailData(columnDefinitions, tabledata);
    }

    protected getDefaultDetailData() {
        let columnDefinitions = [
            new TextColumnDefinition("Cliente", null, true),
            new TextColumnDefinition("Segmento", null, true),
            new DateColumnDefinition("Fecha", (data) => moment(data).format("DD/MM/YYYY")),
            new NumberColumnDefinition("Nº ofrecimientos"),
            new NumberColumnDefinition("Nº candidatos"),
            new TextColumnDefinition("Puesto", null, true),
        ];

        return new OfferingsDetailData(columnDefinitions, []);
    }

    protected performDetailAction(): void {
        if (this.detailDialog) this.detailDialog.open();
    }

    public mergeFilter(filter: any) {
        let detailProcessingArgs = this.detailProcessingArgs || {};
        detailProcessingArgs.filter = detailProcessingArgs.filter || {};
        detailProcessingArgs.filter = { ...detailProcessingArgs.filter, ...filter };
        this.detailProcessingArgs = detailProcessingArgs;
    }

    public ngOnDestroy(): void {
        if (this.raasSubscription) this.raasSubscription.unsubscribe();
        if (this.officeSelectorSubscription) this.officeSelectorSubscription.unsubscribe();
    }
}
